import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import * as log from '../../util/log';

// ================ Action types ================ //

export const ADD_PROMO_CODE_REQUEST = 'app/DiscountCodesPage/ADD_PROMO_CODE_REQUEST';
export const ADD_PROMO_CODE_SUCCESS = 'app/DiscountCodesPage/ADD_PROMO_CODE_SUCCESS';
export const ADD_PROMO_CODE_ERROR = 'app/DiscountCodesPage/ADD_PROMO_CODE_ERROR';

export const UPDATE_PROMO_CODE_REQUEST = 'app/DiscountCodesPage/UPDATE_PROMO_CODE_REQUEST';
export const UPDATE_PROMO_CODE_SUCCESS = 'app/DiscountCodesPage/UPDATE_PROMO_CODE_SUCCESS';
export const UPDATE_PROMO_CODE_ERROR = 'app/DiscountCodesPage/UPDATE_PROMO_CODE_ERROR';

export const DELETE_PROMO_CODE_REQUEST = 'app/DiscountCodesPage/DELETE_PROMO_CODE_REQUEST';
export const DELETE_PROMO_CODE_SUCCESS = 'app/DiscountCodesPage/DELETE_PROMO_CODE_SUCCESS';
export const DELETE_PROMO_CODE_ERROR = 'app/DiscountCodesPage/DELETE_PROMO_CODE_ERROR';

export const CLEAR_PROMO_CODE_ERRORS = 'app/DiscountCodesPage/CLEAR_PROMO_CODE_ERRORS';

// ================ Reducer ================ //

const initialState = {
  addPromoCodeInProgress: false,
  addPromoCodeError: null,
  updatePromoCodeInProgress: false,
  updatePromoCodeError: null,
  deletePromoCodeInProgress: false,
  deletePromoCodeError: null,
};

export default function discountCodesPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case ADD_PROMO_CODE_REQUEST:
      return {
        ...state,
        addPromoCodeInProgress: true,
        addPromoCodeError: null,
      };
    case ADD_PROMO_CODE_SUCCESS:
      return {
        ...state,
        addPromoCodeInProgress: false,
      };
    case ADD_PROMO_CODE_ERROR:
      return {
        ...state,
        addPromoCodeInProgress: false,
        addPromoCodeError: payload,
      };

    case UPDATE_PROMO_CODE_REQUEST:
      return {
        ...state,
        updatePromoCodeInProgress: true,
        updatePromoCodeError: null,
      };
    case UPDATE_PROMO_CODE_SUCCESS:
      return {
        ...state,
        updatePromoCodeInProgress: false,
      };
    case UPDATE_PROMO_CODE_ERROR:
      return {
        ...state,
        updatePromoCodeInProgress: false,
        updatePromoCodeError: payload,
      };

    case DELETE_PROMO_CODE_REQUEST:
      return {
        ...state,
        deletePromoCodeInProgress: true,
        deletePromoCodeError: null,
      };
    case DELETE_PROMO_CODE_SUCCESS:
      return {
        ...state,
        deletePromoCodeInProgress: false,
      };
    case DELETE_PROMO_CODE_ERROR:
      return {
        ...state,
        deletePromoCodeInProgress: false,
        deletePromoCodeError: payload,
      };

    case CLEAR_PROMO_CODE_ERRORS:
      return {
        ...state,
        addPromoCodeError: null,
        updatePromoCodeError: null,
        deletePromoCodeError: null,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const addPromoCodeRequest = () => ({ type: ADD_PROMO_CODE_REQUEST });
export const addPromoCodeSuccess = () => ({ type: ADD_PROMO_CODE_SUCCESS });
export const addPromoCodeError = error => ({
  type: ADD_PROMO_CODE_ERROR,
  payload: error,
  error: true,
});

export const updatePromoCodeRequest = () => ({ type: UPDATE_PROMO_CODE_REQUEST });
export const updatePromoCodeSuccess = () => ({ type: UPDATE_PROMO_CODE_SUCCESS });
export const updatePromoCodeError = error => ({
  type: UPDATE_PROMO_CODE_ERROR,
  payload: error,
  error: true,
});

export const deletePromoCodeRequest = () => ({ type: DELETE_PROMO_CODE_REQUEST });
export const deletePromoCodeSuccess = () => ({ type: DELETE_PROMO_CODE_SUCCESS });
export const deletePromoCodeError = error => ({
  type: DELETE_PROMO_CODE_ERROR,
  payload: error,
  error: true,
});

export const clearPromoCodeErrors = () => ({ type: CLEAR_PROMO_CODE_ERRORS });

// ================ Thunks ================ //

export const addPromoCode = promoCodeData => (dispatch, getState, sdk) => {
  dispatch(addPromoCodeRequest());

  const { currentUser } = getState().user;
  
  if (!currentUser) {
    const error = new Error('User must be logged in to manage promo codes');
    dispatch(addPromoCodeError(storableError(error)));
    return Promise.reject(error);
  }

  // Validate promo code data
  const { code, percentageOff } = promoCodeData;
  const percentageOffNumber = parseInt(percentageOff, 10);
  if (!code || !code.trim() || !Number.isInteger(percentageOffNumber) || percentageOffNumber < 1 || percentageOffNumber > 100) {
    console.log('Invalid promo code data', promoCodeData);
    const error = new Error('Invalid promo code data');
    dispatch(addPromoCodeError(storableError(error)));
    return Promise.reject(error);
  }

  // Get current promo codes from user's privateData
  const { attributes } = currentUser;
  const { profile } = attributes;
  const { privateData } = profile;
  const { promoCodes = [] } = privateData || {};

  // Check if code already exists (case-insensitive)
  const codeExists = promoCodes.some(existingCode => 
    existingCode.code && existingCode.code.toLowerCase() === code.trim().toLowerCase()
  );

  if (codeExists) {
    const error = new Error('A promo code with this name already exists');
    dispatch(addPromoCodeError(storableError(error)));
    return Promise.reject(error);
  }

  // Create new promo code
  const newPromoCode = {
    id: crypto.randomUUID(),
    code: code.trim(),
    percentageOff: percentageOffNumber,
    createdAt: new Date().toISOString(),
    isActive: true,
    public: false
  };

  // Create payload for updating user's profile
  const payload = {
    privateData: {
      ...privateData,
      promoCodes: [...promoCodes, newPromoCode]
    }
  };

  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.currentUser
    .updateProfile(payload, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const updatedUser = entities[0];
      
      dispatch(addPromoCodeSuccess());
      dispatch({ type: 'app/user/CURRENT_USER_SHOW_SUCCESS', payload: updatedUser });
      return updatedUser;
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'add-promo-code-failed');
      dispatch(addPromoCodeError(error));
      throw error;
    });
};

export const updatePromoCode = (promoCodeId, updates) => (dispatch, getState, sdk) => {
  dispatch(updatePromoCodeRequest());

  const { currentUser } = getState().user;
  
  if (!currentUser) {
    const error = new Error('User must be logged in to manage promo codes');
    dispatch(updatePromoCodeError(storableError(error)));
    return Promise.reject(error);
  }

  // Get current promo codes from user's privateData
  const { attributes } = currentUser;
  const { profile } = attributes;
  const { privateData } = profile;
  const { promoCodes = [] } = privateData || {};

  // Handle mutual exclusivity for public field
  let updatedPromoCodes;
  if (updates.public === true) {
    // If setting a promo code to public, set all others to false
    updatedPromoCodes = promoCodes.map(promoCode => {
      if (promoCode.id === promoCodeId) {
        return { ...promoCode, ...updates };
      }
      return { ...promoCode, public: false };
    });
  } else {
    // For other updates, just update the target promo code
    updatedPromoCodes = promoCodes.map(promoCode => {
      if (promoCode.id === promoCodeId) {
        return { ...promoCode, ...updates };
      }
      return promoCode;
    });
  }

  // Check if any code was actually updated
  const codeWasUpdated = updatedPromoCodes.some((code, index) => 
    JSON.stringify(code) !== JSON.stringify(promoCodes[index])
  );

  if (!codeWasUpdated) {
    const error = new Error('Promo code not found');
    dispatch(updatePromoCodeError(storableError(error)));
    return Promise.reject(error);
  }

  // Create payload for updating user's profile
  const payload = {
    privateData: {
      ...privateData,
      promoCodes: updatedPromoCodes
    }
  };

  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.currentUser
    .updateProfile(payload, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const updatedUser = entities[0];
      
      dispatch(updatePromoCodeSuccess());
      dispatch({ type: 'app/user/CURRENT_USER_SHOW_SUCCESS', payload: updatedUser });
      return updatedUser;
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'update-promo-code-failed');
      dispatch(updatePromoCodeError(error));
      throw error;
    });
};

export const deletePromoCode = promoCodeId => (dispatch, getState, sdk) => {
  dispatch(deletePromoCodeRequest());

  const { currentUser } = getState().user;
  
  if (!currentUser) {
    const error = new Error('User must be logged in to manage promo codes');
    dispatch(deletePromoCodeError(storableError(error)));
    return Promise.reject(error);
  }

  // Get current promo codes from user's privateData
  const { attributes } = currentUser;
  const { profile } = attributes;
  const { privateData } = profile;
  const { promoCodes = [] } = privateData || {};

  // Filter out the promo code to delete
  const updatedPromoCodes = promoCodes.filter(promoCode => promoCode.id !== promoCodeId);

  if (updatedPromoCodes.length === promoCodes.length) {
    const error = new Error('Promo code not found');
    dispatch(deletePromoCodeError(storableError(error)));
    return Promise.reject(error);
  }

  // Create payload for updating user's profile
  const payload = {
    privateData: {
      ...privateData,
      promoCodes: updatedPromoCodes
    }
  };

  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.currentUser
    .updateProfile(payload, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const updatedUser = entities[0];
      
      dispatch(deletePromoCodeSuccess());
      dispatch({ type: 'app/user/CURRENT_USER_SHOW_SUCCESS', payload: updatedUser });
      return updatedUser;
    })
    .catch(e => {
      const error = storableError(e);
      log.error(error, 'delete-promo-code-failed');
      dispatch(deletePromoCodeError(error));
      throw error;
    });
};